import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Activity, DollarSign, Database, History,
  TrendingUp, SlidersHorizontal, Layers, Cpu, Building2, Boxes,
  Filter, Calendar, ChevronDown, ChevronUp, ChevronsUpDown, ArrowDown, ArrowUp, Receipt, FileText, X, Coins, Info, Loader2,
} from 'lucide-react';
import { format, subDays } from 'date-fns';
import toast from 'react-hot-toast';
import LineChart from '@/components/reports/charts/LineChart';
import PieChart from '@/components/reports/charts/PieChart';
import SimpleSelect from '@/components/ui/SimpleSelect';
import Pagination from '@/components/ui/Pagination';
import Modal from '@/components/ui/Modal';
import { creditRateService } from '@/services/credit-rate.service';
import costAnalyticsService, {
  CategoryCostRow, CostOverview, ModelCostRow, TenantCostRow, TrendPoint, TenantTrendPoint, CostDetailItem, FeatureCostRow, ExportLogItem,
} from '@/services/costAnalyticsService';
import tenantReportService from '@/services/tenantReportService';
import {
  CATEGORY_META, categoryLabel, categoryColor, featureLabel, exportScopeLabel, prettify, mergeByLowerKey,
  fmtNumber, fmtTokens, PRESETS, TREND_META, TrendMetric, autoGranularity, buildTrendSeries,
  KpiCard, ShareBar, makeCostFmt, CostFormat, CostValue,
} from './aiCostShared';
import { formatDate, formatDateTime } from '@/utils/dateFormat';

// Display helpers, formatters, trend-series transform and the KpiCard / ShareBar
// components live in ./aiCostShared (shared with the print/PDF page).

interface FilterState {
  start_date: string; end_date: string; preset: string;
  tenant_key: string; category: string;
  cost_format: CostFormat;   // display only ($ vs credits) — never sent to the data APIs
}
const today = () => format(new Date(), 'yyyy-MM-dd');
const daysAgo = (n: number) => format(subDays(new Date(), n), 'yyyy-MM-dd');

export default function AiCostTracking() {
  const [filter, setFilter] = useState<FilterState>({
    start_date: '', end_date: '', preset: 'all', tenant_key: '', category: '', cost_format: 'dollar',   // default = All time (full history), shown in $
  });

  const [dollarValue, setDollarValue] = useState<number | undefined>(undefined);
  const [trendMetric, setTrendMetric] = useState<TrendMetric>('total_cost');
  const [showFilters, setShowFilters] = useState(true);
  const [tenantPage, setTenantPage] = useState(1);
  const [tenantPageSize, setTenantPageSize] = useState(10);
  const [detail, setDetail] = useState<CostDetailItem[]>([]);
  const [detailTotal, setDetailTotal] = useState(0);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailPage, setDetailPage] = useState(1);
  const detailPageSize = 20;
  // Cost Detail sort: default = highest cost first (it's a cost report); click Date for newest-first.
  const [detailSort, setDetailSort] = useState<{ field: 'cost' | 'date'; order: 'asc' | 'desc' }>({ field: 'cost', order: 'desc' });
  const toggleDetailSort = (field: 'cost' | 'date') =>
    setDetailSort((s) => s.field === field ? { field, order: s.order === 'asc' ? 'desc' : 'asc' } : { field, order: 'desc' });
  const [expandedRef, setExpandedRef] = useState<string | null>(null);
  // Local category filter inside Cost Details — used only when no top-level category is set.
  const [detailCategory, setDetailCategory] = useState('');

  const [tenants, setTenants] = useState<{ tenant_key: string; name: string }[]>([]);
  const [overview, setOverview] = useState<CostOverview | null>(null);
  const [trend, setTrend] = useState<TrendPoint[]>([]);
  const [tenantTrend, setTenantTrend] = useState<TenantTrendPoint[]>([]);
  const [categories, setCategories] = useState<CategoryCostRow[]>([]);
  const [features, setFeatures] = useState<FeatureCostRow[]>([]);
  const [models, setModels] = useState<ModelCostRow[]>([]);
  const [tenantRows, setTenantRows] = useState<TenantCostRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRolling, setIsRolling] = useState(false);
  const [isPdfExporting, setIsPdfExporting] = useState(false);
  // Export history modal
  const [showHistory, setShowHistory] = useState(false);
  const [exportLog, setExportLog] = useState<ExportLogItem[]>([]);
  const [exportLogTotal, setExportLogTotal] = useState(0);
  const [exportLogLoading, setExportLogLoading] = useState(false);
  const [exportLogPage, setExportLogPage] = useState(1);
  const exportLogPageSize = 10;
  const [error, setError] = useState<string | null>(null);

  // Credit rate popup (1 credit = $X, management-only upsert)
  const [showRateModal, setShowRateModal] = useState(false);
  const [rateValue, setRateValue] = useState<string>('');
  const [rateLoading, setRateLoading] = useState(false);
  const [rateSaving, setRateSaving] = useState(false);

  const openRateModal = async () => {
    setShowRateModal(true);
    setRateLoading(true);
    try {
      const resp = await creditRateService.get();
      const dv = (resp as any)?.data?.dollar_value;
      setRateValue(dv != null ? String(dv) : '');
    } catch {
      setRateValue('');
    } finally {
      setRateLoading(false);
    }
  };

  const saveRate = async () => {
    const val = Number(rateValue);
    if (!Number.isFinite(val) || val <= 0) {
      toast.error('Dollar value must be a number greater than 0.');
      return;
    }
    setRateSaving(true);
    try {
      await creditRateService.save({ dollar_value: val });
      setDollarValue(val);   // refresh the page's rate so credit display updates immediately
      toast.success('Credit rate saved.');
      setShowRateModal(false);
    } catch (e: any) {
      toast.error(e?.response?.data?.error?.error_description || e?.message || 'Failed to save credit rate.');
    } finally {
      setRateSaving(false);
    }
  };

  useEffect(() => {
    tenantReportService.listTenants().then(setTenants).catch(() => setTenants([]));
    // Load the credit rate once so cost values can be shown in credits when selected.
    creditRateService.get()
      .then((resp: any) => setDollarValue(resp?.data?.dollar_value ?? undefined))
      .catch(() => setDollarValue(undefined));
  }, []);

  // Format-aware cost renderer: '$' in dollar mode, credits in credit mode (usd / rate).
  const fmtCost = useMemo(() => makeCostFmt(filter.cost_format, dollarValue), [filter.cost_format, dollarValue]);

  // Compact Y-axis tick (no unit) so labels never wrap/clip — the full unit stays in the tooltip.
  const axisNum = (v: number) =>
    v >= 1_000_000 ? `${(v / 1e6).toFixed(1)}M`
      : v >= 1_000 ? `${(v / 1e3).toFixed(1)}K`
      : Number.isInteger(v) ? String(v) : v.toFixed(2);

  const apiFilter = useMemo(() => ({
    // Empty dates ("All time") → send undefined so the backend applies no date filter.
    start_date: filter.start_date || undefined, end_date: filter.end_date || undefined,
    tenant_keys: filter.tenant_key ? [filter.tenant_key] : undefined,
    service_categories: filter.category ? [filter.category] : undefined,
  }), [filter.start_date, filter.end_date, filter.tenant_key, filter.category]);

  const loadData = useCallback(async (silent = false) => {
    if (!silent) setIsLoading(true);
    setError(null);
    try {
      const gran = autoGranularity(apiFilter.start_date as string, apiFilter.end_date as string);
      // One aggregate line when a tenant is filtered; one line per tenant ("All tenants").
      const trendTask = apiFilter.tenant_keys?.length
        ? costAnalyticsService.getTrend(apiFilter, gran).then((d) => ({ single: d, multi: [] as TenantTrendPoint[] }))
        : costAnalyticsService.getTrendByTenant(apiFilter, gran).then((d) => ({ single: [] as TrendPoint[], multi: d }));
      const [ov, tc, cat, feat, mod, ten] = await Promise.all([
        costAnalyticsService.getOverview(apiFilter),
        trendTask,
        costAnalyticsService.getByCategory(apiFilter),
        costAnalyticsService.getByFeature(apiFilter),
        costAnalyticsService.getByModel(apiFilter),
        costAnalyticsService.getByTenant(apiFilter),
      ]);
      setOverview(ov); setTrend(tc.single); setTenantTrend(tc.multi); setCategories(cat); setFeatures(feat); setModels(mod); setTenantRows(ten);
    } catch (e: any) {
      setError(e?.error?.error_description || e?.message || 'Failed to load cost analytics');
    } finally { if (!silent) setIsLoading(false); }
  }, [apiFilter]);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => { setTenantPage(1); }, [apiFilter]);   // reset tenant pagination when filters change


  const detailCat = filter.category || detailCategory;


  useEffect(() => { setDetailPage(1); setExpandedRef(null); setDetailCategory(''); }, [filter.tenant_key, filter.start_date, filter.end_date, filter.category]);
  useEffect(() => { setDetailPage(1); }, [detailCategory]);
  useEffect(() => {
    if (!filter.tenant_key) { setDetail([]); setDetailTotal(0); return; }
    let cancelled = false;
    setDetailLoading(true);
    costAnalyticsService.getTenantCostDetail(
      filter.tenant_key,
      { start_date: filter.start_date, end_date: filter.end_date, service_categories: detailCat ? [detailCat] : undefined },
      { skip: (detailPage - 1) * detailPageSize, limit: detailPageSize, sort: detailSort.field, order: detailSort.order },
    )
      .then((r) => { if (!cancelled) { setDetail(r.items); setDetailTotal(r.total); } })
      .catch(() => { if (!cancelled) { setDetail([]); setDetailTotal(0); } })
      .finally(() => { if (!cancelled) setDetailLoading(false); });
    return () => { cancelled = true; };
  }, [filter.tenant_key, filter.start_date, filter.end_date, detailCat, detailPage, detailSort]);

  // Auto-refresh: re-fetch the latest summary whenever the user returns to the tab
  // (so an already-open dashboard reflects the background 15-min rollup without a manual button).
  useEffect(() => {
    const refetchOnFocus = () => { if (document.visibilityState === 'visible') loadData(true); };
    document.addEventListener('visibilitychange', refetchOnFocus);
    window.addEventListener('focus', refetchOnFocus);
    return () => {
      document.removeEventListener('visibilitychange', refetchOnFocus);
      window.removeEventListener('focus', refetchOnFocus);
    };
  }, [loadData]);

  const handlePreset = (preset: string, days: number) =>
    setFilter((f) => preset === 'all'
      ? ({ ...f, preset, start_date: '', end_date: '' })            // All time → clear dates (nothing sent)
      // "Last N days" = N days INCLUSIVE of today → start = today − (N−1).
      : ({ ...f, preset, start_date: daysAgo(days - 1), end_date: today() }));

  // Load export history when the modal is open (and on page change).
  useEffect(() => {
    if (!showHistory) return;
    let cancelled = false;
    setExportLogLoading(true);
    costAnalyticsService.getExportLog({ skip: (exportLogPage - 1) * exportLogPageSize, limit: exportLogPageSize })
      .then((r) => { if (!cancelled) { setExportLog(r.items); setExportLogTotal(r.total); } })
      .catch(() => { if (!cancelled) { setExportLog([]); setExportLogTotal(0); } })
      .finally(() => { if (!cancelled) setExportLogLoading(false); });
    return () => { cancelled = true; };
  }, [showHistory, exportLogPage]);

  const handleExportPdf = async () => {
    setIsPdfExporting(true);
    try {
      const { url } = await costAnalyticsService.exportPdf(apiFilter, filter.cost_format);
      window.open(url, '_blank', 'noopener');
    } catch (e: any) {
      toast.error(e?.error?.error_description || e?.message || 'PDF export failed');
    } finally { setIsPdfExporting(false); }
  };

  const handleRunRollup = async () => {
    setIsRolling(true);
    const t = toast.loading('Syncing data…');
    try {
      const summary = await costAnalyticsService.runRollup(false);
      toast.dismiss(t);
      // Every sync refreshes usage counts; `processed` counts only the tenants that had NEW
      // cost activity to roll up. So don't show a scary "0 tenants updated" when nothing was new.
      const n = summary.processed || 0;
      toast.success(n > 0 ? `Data synced — ${n} tenant${n === 1 ? '' : 's'} updated` : 'Data synced — already up to date');
      await loadData();
    } catch (e: any) {
      toast.dismiss(t);
      toast.error(e?.error?.error_description || e?.message || 'Sync failed');
    } finally { setIsRolling(false); }
  };

  const granularity = autoGranularity(filter.start_date, filter.end_date);

  // Trend chart series: one aggregate line when a tenant is filtered, else one line per tenant (top 6 + "Others").
  const { trendLabels, trendSeries } = useMemo(
    () => buildTrendSeries({
      tenantKey: filter.tenant_key, startDate: filter.start_date, endDate: filter.end_date,
      granularity, trend, tenantTrend, trendMetric,
    }),
    [filter.tenant_key, filter.start_date, filter.end_date, granularity, trend, tenantTrend, trendMetric],
  );
  const categoryPie = categories.map((c) => ({ label: categoryLabel(c.service_category), value: c.total_cost || 0, color: categoryColor(c.service_category) }));
  const featureMeta = useMemo(() => {
    const total = features.reduce((s, f) => s + (f.total_cost || 0), 0) || 1;
    const max = Math.max(1, ...features.map((f) => f.total_cost || 0));
    const showUnit = features.some((f) => f.avg_cost_per_item != null);   // only when one tenant is selected
    return { total, max, showUnit };
  }, [features]);
  // Include tenants with zero cost (no AI usage yet) so the full list is visible —
  // unless a specific tenant is filtered. Zero-cost tenants sort to the bottom.
  const allTenantRows = useMemo(() => {
    if (filter.tenant_key) return tenantRows;
    const present = new Set(tenantRows.map((t) => t.tenant_key));
    const zeros = tenants
      .filter((t) => !present.has(t.tenant_key))
      .map((t) => ({ tenant_key: t.tenant_key, tenant_name: t.name, total_cost: 0, request_count: 0, total_tokens: 0, prompt_tokens: 0, completion_tokens: 0 }));
    return [...tenantRows, ...zeros];
  }, [tenantRows, tenants, filter.tenant_key]);
  const totalTenantCost = allTenantRows.reduce((s, t) => s + (t.total_cost || 0), 0) || 1;
  const tenantTotalPages = Math.max(1, Math.ceil(allTenantRows.length / tenantPageSize));
  const pagedTenantRows = allTenantRows.slice((tenantPage - 1) * tenantPageSize, tenantPage * tenantPageSize);
  const costChange = overview?.cost_change_pct;

  const tenantName = tenants.find((t) => t.tenant_key === filter.tenant_key)?.name || filter.tenant_key;
  const clearAll = () => setFilter((f) => ({ ...f, tenant_key: '', category: '', preset: 'all', start_date: '', end_date: '' }));


  const labelCls = 'block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5';

  return (
    <div className="space-y-5 md:space-y-6">
      {/* ── Header (gradient hero, same as Tenant Reports) ── */}
      <div className="relative overflow-hidden bg-gradient-to-br from-purple-600 via-indigo-600 to-indigo-700 rounded-2xl p-6 md:p-7 text-white shadow-lg">
        <div className="absolute -right-8 -top-8 w-40 h-40 bg-white/10 rounded-full blur-2xl" />
        <div className="absolute right-20 bottom-0 w-32 h-32 bg-white/5 rounded-full blur-xl" />
        <div className="relative flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-white/15 backdrop-blur-sm rounded-xl ring-1 ring-white/20">
              {filter.cost_format === 'credit' ? <Coins className="w-7 h-7" /> : <DollarSign className="w-7 h-7" />}
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight">AI Cost Tracking</h1>
              <p className="text-purple-100 text-sm mt-0.5">Cross-tenant AI usage & cost · refreshes every ~15 minutes</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setShowFilters((s) => !s)}
              className={`relative inline-flex items-center gap-2 px-3.5 py-2 rounded-xl text-sm font-medium ring-1 ring-white/20 transition-colors ${showFilters ? 'bg-white text-indigo-700' : 'bg-white/15 hover:bg-white/25 backdrop-blur-sm'}`}>
              <Filter className="w-4 h-4" /> Filters
            </button>
            <button onClick={handleExportPdf} disabled={isPdfExporting || isLoading || !overview}
              className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl bg-white/15 hover:bg-white/25 ring-1 ring-white/20 backdrop-blur-sm text-sm font-medium transition-colors disabled:opacity-50">
              {isPdfExporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />} {isPdfExporting ? 'Generating…' : 'PDF'}
            </button>
            <button onClick={() => setShowHistory(true)} title="Export history"
              className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl bg-white/15 hover:bg-white/25 ring-1 ring-white/20 backdrop-blur-sm text-sm font-medium transition-colors">
              <History className="w-4 h-4" /> History
            </button>
            <button onClick={openRateModal} title="Set credit rate (1 credit = $X)"
              className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl bg-white/15 hover:bg-white/25 ring-1 ring-white/20 backdrop-blur-sm text-sm font-medium transition-colors">
              <Coins className="w-4 h-4" /> Credit Rate
            </button>
            <button onClick={handleRunRollup} disabled={isRolling}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white text-indigo-700 hover:bg-purple-50 text-sm font-semibold shadow-sm transition-colors disabled:opacity-50">
              <Database className={`w-4 h-4 ${isRolling ? 'animate-pulse' : ''}`} /> {isRolling ? 'Syncing…' : 'Sync Data'}
            </button>
          </div>
        </div>
      </div>

      {/* ── Credit Rate popup (1 credit = $X) ── */}
      <Modal
        isOpen={showRateModal}
        onClose={() => setShowRateModal(false)}
        title="Credit Rate"
        subtitle="Set the USD value of 1 credit"
        size="md"
        footer={
          <div className="flex items-center justify-end gap-3">
            <button
              onClick={() => setShowRateModal(false)}
              disabled={rateSaving}
              className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={saveRate}
              disabled={rateSaving || rateLoading}
              className="inline-flex items-center gap-2 px-5 py-2 bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Coins className="w-4 h-4" /> {rateSaving ? 'Saving…' : 'Save'}
            </button>
          </div>
        }
      >
        {rateLoading ? (
          <p className="text-sm text-gray-500">Loading current rate…</p>
        ) : (
          <div>
            <div className="flex items-end gap-3">
              <div className="w-24">
                <label className="block text-sm font-medium text-gray-700 mb-1">Credits</label>
                <input
                  value={1}
                  readOnly
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-500 cursor-not-allowed text-center"
                />
              </div>
              <div className="text-gray-400 pb-2.5 text-lg">=</div>
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">Dollar value (USD)</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                  <input
                    type="number"
                    min={0}
                    step="any"
                    value={rateValue}
                    onChange={(e) => setRateValue(e.target.value)}
                    placeholder="0.05"
                    className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              1 credit is worth this many US dollars. Decimals allowed; must be greater than 0.
            </p>
          </div>
        )}
      </Modal>


      {/* ── Filters (collapsible — opens from the header "Filters" button) ── */}
      {showFilters && (
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5 md:p-6">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <SlidersHorizontal className="w-4 h-4 text-purple-600" />
            <h3 className="text-base font-semibold text-gray-900">Filters</h3>
          </div>
          <button onClick={clearAll} className="text-sm text-gray-500 hover:text-gray-800 font-medium">Clear all</button>
        </div>
        <div className="flex flex-col md:flex-row md:flex-wrap md:items-end gap-4">
          <div><label className={labelCls}>Date range</label>
            <DateRangeSelect preset={filter.preset} startDate={filter.start_date} endDate={filter.end_date}
              onPreset={handlePreset}
              onCustomStart={(v) => setFilter((f) => ({ ...f, preset: 'custom', start_date: v }))}
              onCustomEnd={(v) => setFilter((f) => ({ ...f, preset: 'custom', end_date: v }))} />
          </div>
          <div className="w-full md:w-56"><label className={labelCls}>Tenant</label>
            <SimpleSelect
              value={filter.tenant_key}
              onChange={(v) => setFilter((f) => ({ ...f, tenant_key: (v as string) || '' }))}
              options={[{ label: 'All tenants', value: '' }, ...tenants.map((t) => ({ label: t.name || t.tenant_key, value: t.tenant_key }))]}
              placeholder="All tenants"
              isClearable
            />
          </div>
          <div className="w-full md:w-48"><label className={labelCls}>Category</label>
            <SimpleSelect
              value={filter.category}
              onChange={(v) => setFilter((f) => ({ ...f, category: (v as string) || '' }))}
              options={[{ label: 'All categories', value: '' }, ...Object.keys(CATEGORY_META).map((key) => ({ label: categoryLabel(key), value: key }))]}
              placeholder="All categories"
            />
          </div>
          <div className="w-full md:w-44"><label className={labelCls}>Cost Format</label>
            <SimpleSelect
              value={filter.cost_format}
              onChange={(v) => setFilter((f) => ({ ...f, cost_format: (v as CostFormat) || 'dollar' }))}
              options={[{ label: 'Dollars ($)', value: 'dollar' }, { label: 'Credits', value: 'credit' }]}
              isSearchable={false}
            />
            {filter.cost_format === 'credit' && !dollarValue && (
              <p className="mt-1 text-[11px] text-amber-600">Set a Credit Rate first — showing $ until then.</p>
            )}
          </div>
        </div>
      </div>
      )}

      {/* ── Error (inline, non-blocking) ── */}
      {error && (
        <div className="flex items-center justify-between gap-4 bg-red-50 border border-red-200 rounded-2xl px-5 py-4">
          <div>
            <h3 className="text-red-800 font-semibold text-sm">Couldn’t load cost analytics</h3>
            <p className="text-red-600 text-sm mt-0.5">{error}</p>
          </div>
          <button onClick={() => loadData()} className="shrink-0 px-4 py-2 bg-red-600 text-white rounded-xl hover:bg-red-700 text-sm font-medium">Try Again</button>
        </div>
      )}

      {/* ── KPI cards ── */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
        <KpiCard title="Total Cost" value={
          <span className="inline-flex items-center gap-1.5">
            <CostValue usd={overview?.total_cost} format={filter.cost_format} dollarValue={dollarValue} />
            {filter.cost_format === 'credit' && dollarValue ? (
              <span title={`1 credit = $${dollarValue}`} className="text-gray-400"><Info className="w-3.5 h-3.5" /></span>
            ) : null}
          </span>
        } icon={filter.cost_format === 'credit' ? <Coins className="w-5 h-5" /> : <DollarSign className="w-5 h-5" />} tint="bg-purple-100 text-purple-700"
          change={costChange != null ? Math.round(costChange * 10) / 10 : undefined} trend={costChange == null ? undefined : costChange > 0 ? 'up' : 'down'} loading={isLoading} />
        <KpiCard title="Requests" value={fmtNumber(overview?.request_count)} icon={<Activity className="w-5 h-5" />} tint="bg-cyan-100 text-cyan-700" loading={isLoading} />
        <KpiCard title="Input Tokens" value={fmtTokens(overview?.prompt_tokens)} icon={<ArrowDown className="w-5 h-5" />} tint="bg-emerald-100 text-emerald-700" loading={isLoading} />
        <KpiCard title="Output Tokens" value={fmtTokens(overview?.completion_tokens)} icon={<ArrowUp className="w-5 h-5" />} tint="bg-amber-100 text-amber-700" loading={isLoading} />
      </div>

      {/* ── Trend ── */}
      <Panel icon={<TrendingUp className="w-4 h-4 text-purple-600" />} title="Cost Trend"
        right={
          <div className="flex items-center gap-3">
            <span className="text-xs text-gray-400 capitalize hidden sm:inline" title="Grouping auto-selected from the date range">{granularity}</span>
            <div className="flex gap-1 p-1 bg-gray-100 rounded-xl">
              {(Object.keys(TREND_META) as TrendMetric[]).map((m) => (
                <button key={m} onClick={() => setTrendMetric(m)}
                  className={`px-3 py-1 rounded-lg text-xs font-medium transition-all ${trendMetric === m ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-800'}`}>{TREND_META[m].label}</button>
              ))}
            </div>
          </div>
        }>
        {!isLoading && trendLabels.length === 0 ? <NoData /> : (
          <LineChart labels={trendLabels} series={trendSeries} height={320} loading={isLoading}
            valueFormatter={trendMetric === 'total_cost' ? fmtCost : trendMetric === 'total_tokens' ? fmtTokens : fmtNumber}
            yTickFormatter={axisNum}
            yUnit={trendMetric === 'total_cost' ? (filter.cost_format === 'credit' ? 'Credits' : 'Dollars') : trendMetric === 'total_tokens' ? 'Tokens' : 'Requests'}
            xTickAngle={granularity === 'daily' ? undefined : -30} />
        )}
      </Panel>

      {/* ── Category + Model ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 md:gap-6">
        <Panel icon={<Layers className="w-4 h-4 text-purple-600" />} title="Cost by Service Category">
          {!isLoading && categories.length === 0 ? <NoData /> : (
            <div className="flex justify-center"><PieChart data={categoryPie} size={220} loading={isLoading} valueFormatter={fmtCost} /></div>
          )}
        </Panel>
        <Panel icon={<Cpu className="w-4 h-4 text-purple-600" />} title="Cost by Model">
          {isLoading ? (
            <div className="animate-pulse space-y-3">{[1, 2, 3, 4, 5].map((i) => <div key={i} className="h-8 bg-gray-100 rounded" />)}</div>
          ) : models.length === 0 ? <NoData /> : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs font-semibold uppercase tracking-wide text-gray-500 bg-gray-50/70">
                    <th className="px-3 py-2.5">Model</th>
                    <th className="px-3 py-2.5">Category</th>
                    <th className="px-3 py-2.5 text-right">Input</th>
                    <th className="px-3 py-2.5 text-right">Output</th>
                    <th className="px-3 py-2.5 text-right">Cost</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {models.map((m) => (
                    <tr key={`${m.model}-${m.service_category}`} className="hover:bg-gray-50/60 transition-colors">
                      <td className="px-3 py-2.5 font-medium text-gray-900">{m.model}</td>
                      <td className="px-3 py-2.5">
                        <span className="inline-block px-2 py-0.5 rounded-full text-xs" style={{ backgroundColor: `${categoryColor(m.service_category)}1a`, color: categoryColor(m.service_category) }}>{categoryLabel(m.service_category)}</span>
                      </td>
                      <td className="px-3 py-2.5 text-right text-emerald-600">{fmtTokens(m.prompt_tokens)}</td>
                      <td className="px-3 py-2.5 text-right text-amber-600">{fmtTokens(m.completion_tokens)}</td>
                      <td className="px-3 py-2.5 text-right font-semibold text-gray-900 whitespace-nowrap">{fmtCost(m.total_cost)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {!isLoading && models.length > 0 && (
            <p className="text-xs text-gray-400 mt-3">Input / Output show text tokens — consumed only by text-generation tasks (e.g. course &amp; certification generation, AI chat). Image generation, web search, speech &amp; storage don&apos;t use tokens, so they show 0 Input/Output but still incur cost.</p>
          )}
        </Panel>
      </div>

      {/* ── Cost by Feature (what produced the cost — Course / Cert / Document / …) ── */}
      <Panel icon={<Boxes className="w-4 h-4 text-purple-600" />} title="Cost by Feature">
        {isLoading ? (
          <div className="animate-pulse space-y-3">{[1, 2, 3, 4].map((i) => <div key={i} className="h-9 bg-gray-100 rounded" />)}</div>
        ) : features.length === 0 ? <NoData /> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs font-semibold uppercase tracking-wide text-gray-500 bg-gray-50/70">
                  <th className="px-3 py-2.5">Feature</th>
                  <th className="px-3 py-2.5 w-1/3">Spend</th>
                  <th className="px-3 py-2.5 text-right">Requests</th>
                  <th className="px-3 py-2.5 text-right">Cost</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {features.map((f) => (
                  <tr key={f.feature_type} className="hover:bg-gray-50/60 transition-colors">
                    <td className="px-3 py-2.5 font-medium text-gray-900">{featureLabel(f.feature_type)}</td>
                    <td className="px-3 py-2.5 pr-6">
                      <ShareBar pct={(f.total_cost / featureMeta.total) * 100} />
                    </td>
                    <td className="px-3 py-2.5 text-right text-gray-600">{fmtNumber(f.request_count)}</td>
                    <td className="px-3 py-2.5 text-right font-semibold text-gray-900 whitespace-nowrap">{fmtCost(f.total_cost)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      {/* ── Tenant leaderboard (only for the "All tenants" view) ── */}
      {!filter.tenant_key && (
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="flex items-center gap-2 px-5 md:px-6 py-4 border-b border-gray-100">
          <Building2 className="w-4 h-4 text-purple-600" />
          <h3 className="text-base font-semibold text-gray-900">Cost by Tenant</h3>
        </div>
        {isLoading ? (
          <div className="p-5 space-y-3">{[1, 2, 3, 4, 5].map((i) => <div key={i} className="h-9 bg-gray-100 rounded-lg animate-pulse" />)}</div>
        ) : allTenantRows.length === 0 ? (
          <div className="text-center py-16 px-6">
            <div className="inline-flex p-4 bg-gray-50 rounded-2xl mb-4"><Building2 className="w-10 h-10 text-gray-300" /></div>
            <h4 className="text-gray-900 font-semibold mb-1">No tenant cost yet</h4>
            <p className="text-gray-500 text-sm">Click <span className="font-medium">Sync Data</span> or widen the date range.</p>
          </div>
        ) : (
          <>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs font-semibold uppercase tracking-wide text-gray-500 bg-gray-50/70">
                  <th className="px-5 md:px-6 py-3 w-12">#</th>
                  <th className="px-4 py-3">Tenant</th>
                  <th className="px-4 py-3 w-1/4">Spend</th>
                  <th className="px-4 py-3 text-right">Requests</th>
                  <th className="px-4 py-3 text-right">Input</th>
                  <th className="px-4 py-3 text-right">Output</th>
                  <th className="px-5 md:px-6 py-3 text-right">Cost</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {pagedTenantRows.map((t, i) => {
                  const rank = (tenantPage - 1) * tenantPageSize + i + 1;
                  return (
                  <tr key={t.tenant_key} className="hover:bg-gray-50/60 transition-colors">
                    <td className="px-5 md:px-6 py-3.5">
                      <span className={`w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold ${rank === 1 ? 'bg-amber-100 text-amber-700' : rank === 2 ? 'bg-gray-200 text-gray-600' : rank === 3 ? 'bg-orange-100 text-orange-700' : 'bg-gray-100 text-gray-400'}`}>{rank}</span>
                    </td>
                    <td className="px-4 py-3.5 font-medium text-gray-900">{t.tenant_name || t.tenant_key}</td>
                    <td className="px-4 py-3.5 pr-6">
                      <ShareBar pct={(t.total_cost / totalTenantCost) * 100} />
                    </td>
                    <td className="px-4 py-3.5 text-right text-gray-600">{fmtNumber(t.request_count)}</td>
                    <td className="px-4 py-3.5 text-right text-emerald-600">{fmtTokens(t.prompt_tokens)}</td>
                    <td className="px-4 py-3.5 text-right text-amber-600">{fmtTokens(t.completion_tokens)}</td>
                    <td className="px-5 md:px-6 py-3.5 text-right font-semibold text-gray-900 whitespace-nowrap">{fmtCost(t.total_cost)}</td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {allTenantRows.length > 0 && (
            <Pagination
              currentPage={tenantPage}
              totalPages={tenantTotalPages}
              totalItems={allTenantRows.length}
              itemsPerPage={tenantPageSize}
              onPageChange={setTenantPage}
              onItemsPerPageChange={(n) => { setTenantPageSize(n); setTenantPage(1); }}
            />
          )}
          </>
        )}
      </div>
      )}

      {/* ── Per-item cost detail (shown when a single tenant is selected) ── */}
      {filter.tenant_key && (
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="flex items-center justify-between gap-3 px-5 md:px-6 py-4 border-b border-gray-100">
            <div className="flex items-center gap-2">
              <Receipt className="w-4 h-4 text-purple-600" />
              <h3 className="text-base font-semibold text-gray-900">Cost Detail — {tenantName}</h3>
              <span className="text-xs text-gray-400 ml-1">{detailTotal} items</span>
            </div>
            {/* Local category filter — shown only when no top-level category is selected. */}
            {!filter.category && (
              <div className="w-44 shrink-0">
                <SimpleSelect
                  value={detailCategory}
                  onChange={(v) => setDetailCategory((v as string) || '')}
                  options={[{ label: 'All categories', value: '' }, ...Object.keys(CATEGORY_META).map((key) => ({ label: categoryLabel(key), value: key }))]}
                  placeholder="All categories"
                />
              </div>
            )}
          </div>
          {detailLoading ? (
            <div className="p-5 space-y-3">{[1, 2, 3, 4, 5].map((i) => <div key={i} className="h-9 bg-gray-100 rounded-lg animate-pulse" />)}</div>
          ) : detail.length === 0 ? (
            <div className="text-center py-12 px-6">
              <Receipt className="w-9 h-9 text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-gray-500">No items for this tenant in the selected range.</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs font-semibold uppercase tracking-wide text-gray-500 bg-gray-50/70">
                      <th className="px-5 md:px-6 py-3">Item</th>
                      <th className="px-4 py-3">Type</th>
                      <th className="px-4 py-3 cursor-pointer select-none hover:text-gray-700" onClick={() => toggleDetailSort('date')}><span className="inline-flex items-center gap-1">Date {detailSort.field === 'date' ? (detailSort.order === 'asc' ? <ChevronUp className="w-3.5 h-3.5 text-gray-800" /> : <ChevronDown className="w-3.5 h-3.5 text-gray-800" />) : <ChevronsUpDown className="w-3.5 h-3.5 text-gray-400" />}</span></th>
                      <th className="px-4 py-3 text-right">Input</th>
                      <th className="px-4 py-3 text-right">Output</th>
                      <th className="px-5 md:px-6 py-3 text-right cursor-pointer select-none hover:text-gray-700" onClick={() => toggleDetailSort('cost')}><span className="inline-flex items-center gap-1 justify-end">Cost {detailSort.field === 'cost' ? (detailSort.order === 'asc' ? <ChevronUp className="w-3.5 h-3.5 text-gray-800" /> : <ChevronDown className="w-3.5 h-3.5 text-gray-800" />) : <ChevronsUpDown className="w-3.5 h-3.5 text-gray-400" />}</span></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {detail.map((it) => {
                      const open = expandedRef === it.reference_id;
                      const stages = Object.entries(mergeByLowerKey(it.cost_by_stage)).filter(([, v]) => v.cost > 0).sort((a, b) => b[1].cost - a[1].cost);
                      const services = Object.entries(mergeByLowerKey(it.cost_by_service)).filter(([, v]) => v.cost > 0).sort((a, b) => b[1].cost - a[1].cost);
                      const breakdown = stages.length ? stages : services;
                      return (
                        <React.Fragment key={it.reference_id}>
                          <tr className="hover:bg-gray-50/60 transition-colors cursor-pointer" onClick={() => setExpandedRef(open ? null : it.reference_id)}>
                            <td className="px-5 md:px-6 py-3.5 font-medium text-gray-900">
                              <span className="inline-flex items-center gap-1.5">
                                <ChevronDown className={`w-3.5 h-3.5 text-gray-400 transition-transform ${open ? 'rotate-180' : '-rotate-90'}`} />
                                {it.name}
                              </span>
                            </td>
                            <td className="px-4 py-3.5"><span className="inline-block px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-600">{featureLabel(it.feature_type)}</span></td>
                            <td className="px-4 py-3.5 text-gray-500 whitespace-nowrap">{it.date ? formatDate(it.date, 'dd/mm/yyyy') : '—'}</td>
                            <td className="px-4 py-3.5 text-right text-emerald-600">{fmtTokens(it.prompt_tokens)}</td>
                            <td className="px-4 py-3.5 text-right text-amber-600">{fmtTokens(it.completion_tokens)}</td>
                            <td className="px-5 md:px-6 py-3.5 text-right font-semibold text-gray-900 whitespace-nowrap">{fmtCost(it.total_cost)}</td>
                          </tr>
                          {open && (
                            <tr className="bg-gray-50/40">
                              <td colSpan={6} className="px-5 md:px-6 py-3">
                                <div className="flex flex-wrap gap-2">
                                  {breakdown.length === 0 ? (
                                    <span className="text-xs text-gray-400">No breakdown available.</span>
                                  ) : breakdown.map(([k, v]) => (
                                    <span key={k} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-white border border-gray-200 text-xs">
                                      <span className="text-gray-600">{prettify(k)}</span>
                                      <span className="font-semibold text-gray-900 whitespace-nowrap">{fmtCost(v.cost || 0)}</span>
                                    </span>
                                  ))}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {Math.ceil(detailTotal / detailPageSize) > 1 && (
                <Pagination
                  currentPage={detailPage}
                  totalPages={Math.ceil(detailTotal / detailPageSize)}
                  totalItems={detailTotal}
                  itemsPerPage={detailPageSize}
                  onPageChange={setDetailPage}
                  showItemsPerPage={false}
                />
              )}
            </>
          )}
        </div>
      )}

      {/* ── Export history (who downloaded a PDF, when, scope) ── */}
      {showHistory && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setShowHistory(false)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[80vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <History className="w-4 h-4 text-purple-600" />
                <h3 className="text-base font-semibold text-gray-900">Export History</h3>
                <span className="text-xs text-gray-400 ml-1">{exportLogTotal} export{exportLogTotal === 1 ? '' : 's'}</span>
              </div>
              <button onClick={() => setShowHistory(false)} className="text-gray-400 hover:text-gray-700"><X className="w-5 h-5" /></button>
            </div>
            <div className="overflow-auto">
              {exportLogLoading ? (
                <div className="p-5 space-y-3">{[1, 2, 3, 4].map((i) => <div key={i} className="h-9 bg-gray-100 rounded-lg animate-pulse" />)}</div>
              ) : exportLog.length === 0 ? (
                <div className="text-center py-12 text-sm text-gray-500">No exports yet.</div>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs font-semibold uppercase tracking-wide text-gray-500 bg-gray-50/70">
                      <th className="px-5 py-3">User</th>
                      <th className="px-4 py-3">Generated</th>
                      <th className="px-4 py-3">Scope</th>
                      <th className="px-5 py-3 text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {exportLog.map((e, i) => (
                      <tr key={i} className="hover:bg-gray-50/60 transition-colors">
                        <td className="px-5 py-3">
                          <div className="font-medium text-gray-900">{e.user_name || e.user_email || 'Unknown'}</div>
                          {e.user_email && e.user_name && <div className="text-xs text-gray-400">{e.user_email}</div>}
                        </td>
                        <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{e.createdAt ? formatDateTime(e.createdAt, true) : '—'}</td>
                        <td className="px-4 py-3 text-gray-600">{exportScopeLabel(e)}</td>
                        <td className="px-5 py-3 text-right">
                          <a href={e.pdf_url} target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:text-purple-800 font-medium">Open</a>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            {Math.ceil(exportLogTotal / exportLogPageSize) > 1 && (
              <div className="border-t border-gray-100">
                <Pagination
                  currentPage={exportLogPage}
                  totalPages={Math.ceil(exportLogTotal / exportLogPageSize)}
                  totalItems={exportLogTotal}
                  itemsPerPage={exportLogPageSize}
                  onPageChange={setExportLogPage}
                  showItemsPerPage={false}
                />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ---- presentational helpers ------------------------------------------------

function Panel({ icon, title, right, children }: { icon: React.ReactNode; title: string; right?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5 md:p-6">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">{icon}<h3 className="text-base font-semibold text-gray-900">{title}</h3></div>
        {right}
      </div>
      {children}
    </div>
  );
}

function DateRangeSelect({
  preset, startDate, endDate, onPreset, onCustomStart, onCustomEnd,
}: {
  preset: string; startDate: string; endDate: string;
  onPreset: (key: string, days: number) => void;
  onCustomStart: (v: string) => void; onCustomEnd: (v: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const dateFromRef = useRef<HTMLInputElement>(null);
  const dateToRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    const handler = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const fmtShort = (d: string) => { try { return format(new Date(`${d}T00:00:00`), 'MMM d'); } catch { return d; } };
  const label = preset === 'custom'
    ? `${fmtShort(startDate)} – ${fmtShort(endDate)}`
    : (PRESETS.find((p) => p.key === preset)?.label || 'Select range');

  return (
    <div className="relative" ref={ref}>
      <button type="button" onClick={() => setOpen((o) => !o)}
        className="w-full md:w-60 flex items-center justify-between gap-2 px-3 py-2.5 border border-gray-300 rounded-xl text-sm bg-white text-gray-700 hover:border-purple-300 transition-colors">
        <span className="flex items-center gap-2 truncate"><Calendar className="w-4 h-4 text-gray-400 shrink-0" />{label}</span>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute z-20 mt-2 w-72 bg-white rounded-xl border border-gray-200 shadow-lg p-2">
          <div className="space-y-0.5">
            {PRESETS.map((p) => (
              <button key={p.key} onClick={() => { onPreset(p.key, p.days); setOpen(false); }}
                className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${preset === p.key ? 'bg-purple-50 text-purple-700 font-medium' : 'text-gray-700 hover:bg-gray-50'}`}>{p.label}</button>
            ))}
          </div>
          <div className="border-t border-gray-100 mt-2 pt-2">
            <div className={`px-3 mb-1.5 text-xs font-semibold uppercase tracking-wide ${preset === 'custom' ? 'text-purple-600' : 'text-gray-400'}`}>Custom range</div>
            {/* dd/mm/yyyy display with hidden native picker — same pattern as
                TenantReports / EmailLogPage (formatDate util is the app standard). */}
            <div className="px-2 pb-1 space-y-2">
              <div>
                <label className="block text-[11px] text-gray-400 mb-1">From</label>
                <div className="relative cursor-pointer" onClick={() => dateFromRef.current?.showPicker()}>
                  <div className="flex items-center gap-2 w-full px-2.5 py-2 border border-gray-300 rounded-lg text-sm bg-white">
                    <Calendar className="w-4 h-4 text-gray-400 shrink-0" />
                    <span className={startDate ? 'text-gray-900' : 'text-gray-400'}>
                      {startDate ? formatDate(startDate, 'dd/mm/yyyy') : 'dd/mm/yyyy'}
                    </span>
                  </div>
                  <input ref={dateFromRef} type="date" value={startDate} max={endDate}
                    onChange={(e) => onCustomStart(e.target.value)}
                    className="absolute inset-0 w-full h-full opacity-0 pointer-events-none" />
                </div>
              </div>
              <div>
                <label className="block text-[11px] text-gray-400 mb-1">To</label>
                <div className="relative cursor-pointer" onClick={() => dateToRef.current?.showPicker()}>
                  <div className="flex items-center gap-2 w-full px-2.5 py-2 border border-gray-300 rounded-lg text-sm bg-white">
                    <Calendar className="w-4 h-4 text-gray-400 shrink-0" />
                    <span className={endDate ? 'text-gray-900' : 'text-gray-400'}>
                      {endDate ? formatDate(endDate, 'dd/mm/yyyy') : 'dd/mm/yyyy'}
                    </span>
                  </div>
                  <input ref={dateToRef} type="date" value={endDate} min={startDate} max={today()}
                    onChange={(e) => onCustomEnd(e.target.value)}
                    className="absolute inset-0 w-full h-full opacity-0 pointer-events-none" />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function NoData() {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <div className="inline-flex p-4 bg-gray-50 rounded-2xl mb-3"><Database className="w-9 h-9 text-gray-300" /></div>
      <p className="text-sm text-gray-500">No data for this range yet.</p>
    </div>
  );
}

